import type { Status } from '../types';

const API_URL = (() => {
  const env = import.meta.env.VITE_API_URL as string | undefined;
  if (env) return env;
  if (import.meta.env.DEV) return 'http://localhost:3001/api';
  throw new Error('VITE_API_URL is required at build time. Set it in apps/mini-app/.env before pnpm build.');
})();

class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

let getToken: () => string | null = () => null;
let getActiveWorkshopId: () => string | null = () => null;

export function configureApi(opts: { getToken: () => string | null; getActiveWorkshopId: () => string | null }) {
  getToken = opts.getToken;
  getActiveWorkshopId = opts.getActiveWorkshopId;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${path}`, { ...init, headers });

  const ct = res.headers.get('content-type') ?? '';
  const data = ct.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    const message = (data as { message?: string })?.message || res.statusText;
    throw new ApiError(message, res.status, data);
  }

  return data as T;
}

function withWorkshop(path: string): string {
  const wid = getActiveWorkshopId();
  return wid ? `/workshops/${wid}${path}` : path;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  withWorkshop,

  // Auth
  telegramAuth: (initData: string) =>
    request<{ accessToken: string; refreshToken: string; user: any }>('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ initData }),
    }),

  // Me
  me: () => request<{ user: any; activeWorkshop: any; workshops: any[] }>('/me'),
  myWorkshops: () => request<any[]>('/me/workshops'),
  switchWorkshop: (workshopId: string) =>
    request<{ workshop: any }>('/workshops/active/switch', {
      method: 'POST',
      body: JSON.stringify({ workshopId }),
    }),

  // Stats
  today: () => request<import('../types').TodayStats>('/stats/today'),
  overview: (days = 30) => request<any>(`/stats/overview?days=${days}`),
  revenue: (days = 14) => request<Array<{ date: string; orders: number; revenue: number; paid: number }>>(`/stats/revenue?days=${days}`),
  topServices: (days = 30) => request<{ services: any[]; logTypes: Record<string, number> }>(`/stats/top-services?days=${days}`),
  peakHours: () => request<Array<{ hour: number; received: number; scheduled: number }>>('/stats/peak-hours'),
  customers: (days = 30) => request<{ total: number; new: number; returning: number; topSpenders: any[] }>(`/stats/customers?days=${days}`),

  // Orders
  listOrders: (params: { status?: Status; page?: number; pageSize?: number; q?: string } = {}) => {
    const sp = new URLSearchParams();
    if (params.status) sp.set('status', params.status);
    if (params.page) sp.set('page', String(params.page));
    if (params.pageSize) sp.set('pageSize', String(params.pageSize));
    if (params.q) sp.set('q', params.q);
    return request<import('../types').Paginated<import('../types').Order>>(
      `${withWorkshop('/orders')}?${sp}`,
    );
  },
  getOrder: (id: string) => request<import('../types').Order>(withWorkshop(`/orders/${id}`)),
  updateOrderStatus: (id: string, status: Status, note?: string) =>
    request<import('../types').Order>(withWorkshop(`/orders/${id}/status`), {
      method: 'PATCH',
      body: JSON.stringify({ status, note }),
    }),
  addLog: (orderId: string, body: { type: string; description: string; qty?: number; unitPrice?: number }) =>
    request(withWorkshop(`/orders/${orderId}/logs`), {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteLog: (orderId: string, logId: string) =>
    request(withWorkshop(`/orders/${orderId}/logs/${logId}`), { method: 'DELETE' }),
  addPayment: (
    orderId: string,
    body: { method: string; amount: number; externalId?: string },
  ) =>
    request(withWorkshop(`/orders/${orderId}/payments`), {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Cars / Clients
  listCars: (q?: string) => {
    const sp = new URLSearchParams();
    if (q) sp.set('q', q);
    return request<import('../types').Paginated<import('../types').Car>>(
      `${withWorkshop('/cars')}?${sp}`,
    );
  },
  getCarPassport: (plate: string) => request(`/cars/passport/${encodeURIComponent(plate)}`),

  // Marketplace
  searchWorkshops: (params: { q?: string; city?: string; page?: number; pageSize?: number } = {}) => {
    const sp = new URLSearchParams();
    if (params.q) sp.set('q', params.q);
    if (params.city) sp.set('city', params.city);
    if (params.page) sp.set('page', String(params.page));
    if (params.pageSize) sp.set('pageSize', String(params.pageSize));
    return request<import('../types').Paginated<import('../types').Workshop>>(
      `/marketplace/workshops?${sp}`,
    );
  },
  getWorkshop: (id: string) => request<any>(`/marketplace/workshops/${id}`),
  getWorkshopSlots: (id: string) =>
    request<{ date: string; times: string[] }[]>(`/marketplace/workshops/${id}/slots`),
  getWorkshopReviews: (id: string) => request<any[]>(`/workshops/${id}/reviews`),
  getWorkshopServices: (id: string) =>
    request<any[]>(`/workshops/${id}/services/public`),

  // Bookings
  myBookings: () => request<any[]>('/my/bookings'),
  createBooking: (body: any) =>
    request('/bookings', { method: 'POST', body: JSON.stringify(body) }),
  cancelBooking: (id: string) => request(`/bookings/${id}/cancel`, { method: 'PATCH' }),

  // Reviews
  createReview: (orderId: string, body: { rating: number; text?: string; photos?: string[] }) =>
    request(`/orders/${orderId}/review`, { method: 'POST', body: JSON.stringify(body) }),

  // My cars
  myCars: () => request<any[]>('/me/cars'),
  addMyCar: (body: {
    plate: string;
    vin?: string;
    brand: string;
    model: string;
    year?: number;
    color?: string;
    ownerName?: string;
    ownerPhone?: string;
  }) => request<any>('/me/cars', { method: 'POST', body: JSON.stringify(body) }),
  updateMyCar: (id: string, body: Partial<{
    plate: string;
    vin: string;
    brand: string;
    model: string;
    year: number;
    color: string;
  }>) => request<any>(`/me/cars/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteMyCar: (id: string) => request<{ ok: boolean }>(`/me/cars/${id}`, { method: 'DELETE' }),
  myOrders: () => request<any[]>('/me/orders'),
  mySubscription: () => request<any>('/me/subscription'),
};

export { ApiError };
